/* Linear algebra for the calibration solve and the on-site warp.
   Pure: no DOM, no three, no app state — so it can be run and checked outside a
   browser, which is how the solver is regression-tested against a synthetic
   photo of a known camera pose. */

// Gaussian elimination with partial pivoting. A is n×n row-major, b length n.
export function solveLinear(A, b, n){
  const M = A.map((r,i)=>r.concat([b[i]]));
  for(let c=0;c<n;c++){
    let p=c;
    for(let r=c+1;r<n;r++) if(Math.abs(M[r][c])>Math.abs(M[p][c])) p=r;
    if(Math.abs(M[p][c])<1e-14) return null;
    [M[c],M[p]]=[M[p],M[c]];
    const pv=M[c][c];
    for(let j=c;j<=n;j++) M[c][j]/=pv;
    for(let r=0;r<n;r++){
      if(r===c) continue;
      const f=M[r][c]; if(!f) continue;
      for(let j=c;j<=n;j++) M[r][j]-=f*M[c][j];
    }
  }
  return M.map(r=>r[n]);
}

// 4-point homography: src[i]=[x,y] -> dst[i]=[X,Y]. Returns 9 values row-major, h22=1.
export function homography(src, dst){
  const A=[], b=[];
  for(let i=0;i<4;i++){
    const [x,y]=src[i], [X,Y]=dst[i];
    A.push([x,y,1,0,0,0,-X*x,-X*y]); b.push(X);
    A.push([0,0,0,x,y,1,-Y*x,-Y*y]); b.push(Y);
  }
  const h = solveLinear(A,b,8);
  return h ? h.concat([1]) : null;
}

export const dot=(a,b)=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
export const len=a=>Math.sqrt(dot(a,a));
export const nrm=a=>{const l=len(a); return [a[0]/l,a[1]/l,a[2]/l];};
export const crs=(a,b)=>[a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];

// Convex + correctly wound (TL,TR,BR,BL is clockwise in y-down image space).
export function quadCheck(p){
  let sign=0;
  for(let i=0;i<4;i++){
    const a=p[i], b=p[(i+1)%4], c=p[(i+2)%4];
    const cr=(b[0]-a[0])*(c[1]-b[1])-(b[1]-a[1])*(c[0]-b[0]);
    if(Math.abs(cr)<1e-9) return {ok:false,msg:'Three corners are in a straight line. Spread them out to form a proper quad.'};
    const s=Math.sign(cr);
    if(!sign) sign=s; else if(s!==sign) return {ok:false,msg:'The corners cross over each other. Drag them so they trace the screen edge in TL → TR → BR → BL order.'};
  }
  let A=0;
  for(let i=0;i<4;i++){const a=p[i],b=p[(i+1)%4]; A+=a[0]*b[1]-b[0]*a[1];}
  if(A<=0) return {ok:false,msg:'The corner order is mirrored. TL belongs at the top-left of the screen as you see it in the photo.'};
  return {ok:true};
}

/* Apply a row-major homography to a point. */
export function applyH(H, x, y){
  const w = H[6]*x + H[7]*y + H[8];
  return [(H[0]*x + H[1]*y + H[2])/w, (H[3]*x + H[4]*y + H[5])/w];
}
